import { CONFIDENCE_TONE } from "./constants";
import { formatLegLine } from "./formatters";

// Slip Summary card rendered above the per-leg cards. Aggregate view of
// leg_count / analyzed_count / average PA Score / strongest / weakest.
// Copy and testids preserved verbatim.
export function SlipSummaryCard({ summary }) {
  if (!summary) return null;
  const {
    leg_count = 0,
    analyzed_count = 0,
    unsupported_count = 0,
    unmatched_count = 0,
    strongest_leg = null,
    weakest_leg = null,
    average_pa_score = null,
    overall_confidence = "Low",
  } = summary;
  const tone = CONFIDENCE_TONE[overall_confidence] || CONFIDENCE_TONE.Low;
  const hasAnalyzed = analyzed_count > 0;

  return (
    <section
      data-testid="analyzer-slip-summary"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
          Slip Summary
        </h3>
        <span
          data-testid="analyzer-slip-summary-confidence"
          className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-full font-display"
          style={{ backgroundColor: tone.bg, color: tone.text }}
        >
          Data Confidence · {overall_confidence}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">Legs</dt>
          <dd className="text-slate-900 text-sm font-mono-num" data-testid="analyzer-slip-summary-legs">
            {leg_count}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">Analyzed</dt>
          <dd className="text-slate-900 text-sm font-mono-num" data-testid="analyzer-slip-summary-analyzed">
            {analyzed_count}
            {unsupported_count + unmatched_count > 0 && (
              <span className="text-slate-500 text-xs ml-1">
                (skipped {unsupported_count + unmatched_count})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Avg PA Score
          </dt>
          <dd className="text-slate-900 text-sm font-mono-num" data-testid="analyzer-slip-summary-average">
            {hasAnalyzed && typeof average_pa_score === "number" ? `${average_pa_score} / 100` : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
            Overall
          </dt>
          <dd className="text-slate-900 text-sm font-mono-num">
            {hasAnalyzed ? `${analyzed_count} of ${leg_count} scored` : "No legs scored"}
          </dd>
        </div>
      </dl>

      {hasAnalyzed && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div data-testid="analyzer-slip-summary-strongest">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Strongest Leg
            </dt>
            <dd className="text-slate-900 text-sm">
              {strongest_leg ? (
                <>
                  <span className="font-mono-num">
                    {strongest_leg.pa_score} · {strongest_leg.pa_label}
                  </span>{" "}
                  <span className="text-slate-600">— {formatLegLine(strongest_leg)}</span>
                </>
              ) : "—"}
            </dd>
          </div>
          <div data-testid="analyzer-slip-summary-weakest">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Weakest Leg
            </dt>
            <dd className="text-slate-900 text-sm">
              {weakest_leg ? (
                <>
                  <span className="font-mono-num">
                    {weakest_leg.pa_score} · {weakest_leg.pa_label}
                  </span>{" "}
                  <span className="text-slate-600">— {formatLegLine(weakest_leg)}</span>
                </>
              ) : "—"}
            </dd>
          </div>
        </div>
      )}

      <p
        className="mt-5 text-xs text-slate-500 border-t border-slate-200 pt-4"
        data-testid="analyzer-slip-summary-disclaimer"
      >
        Slip Summary aggregates verified per-leg research. The average PA Score
        is not a win probability.
      </p>
    </section>
  );
}
